'use client';

import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';
import { Image as ProductImage } from '@medusajs/medusa';
import {
  PricedProduct,
  PricedVariant,
} from '@medusajs/medusa/dist/types/pricing';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/scss';
import 'swiper/scss/navigation';
import styles from './styles.module.scss';

type ProductGalleryProps = {
  images: ProductImage[];
  title: string | undefined;
  thumbnail: string | null | undefined;
  selectedVariant: PricedVariant | undefined;
  product: PricedProduct;
};

const ProductGallery = ({
  images,
  title,
  selectedVariant,
  product,
}: ProductGalleryProps) => {
  const [mainPhoto, setMainPhoto] = useState(images[0]?.url || null);
  const [openModal, setOpenModal] = useState(false);
  const [slideNumber, setSlideNumber] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const disableImgToggle = product.title?.toLowerCase().includes('koszulka');

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const prevSlide = () => {
    setSlideNumber(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setSlideNumber(prev => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleGalleryPhotoClick = (url: string, index: number) => {
    if (url !== mainPhoto) {
      setMainPhoto(url);
    }
    setSlideNumber(index);
  };

  // handle gallery scroll on variant change
  useEffect(() => {
    // do not toggle imgs for shirts
    if (disableImgToggle) return;

    if (swiperInstance && selectedVariant) {
      const variantPosition = product.variants.indexOf(selectedVariant);
      const imagePosition =
        variantPosition === INITIAL_VARIANT_POSITION
          ? INITIAL_VARIANT_POSITION
          : variantPosition * IMAGES_PER_PRODUCT;

      // return if there is not enough images
      if (images.length < imagePosition) {
        return;
      }

      const mainImageToDisplay: string | undefined = images[imagePosition]?.url;

      if (mainImageToDisplay) {
        setMainPhoto(mainImageToDisplay);
      }

      swiperInstance.slideTo(imagePosition);
    }
  }, [
    selectedVariant,
    product,
    swiperInstance,
    setMainPhoto,
    images,
    disableImgToggle,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.photoWrapper} onClick={handleOpenModal}>
        {mainPhoto && (
          <Image
            src={mainPhoto}
            alt={`Zdjęcie produktu ${title}`}
            width={1400}
            height={1050}
            quality={100}
          />
        )}
      </div>
      <div className={styles.galleryContainer}>
        <Swiper
          slidesPerView={isMobile ? 2.5 : 4.4}
          initialSlide={0}
          spaceBetween={isMobile ? 8 : 22}
          centeredSlides={false}
          scrollbar={{
            hide: true,
          }}
          modules={[Navigation, Scrollbar]}
          navigation
          onSwiper={setSwiperInstance}
          className="swiper gallery-product-swiper"
        >
          {images.map((image, index) => (
            <SwiperSlide key={index}>
              <div
                className={styles.imageWrapper}
                onClick={() => handleGalleryPhotoClick(image.url, index)}
              >
                <Image
                  src={image.url}
                  alt={`Zdjęcie produktu ${title}`}
                  width={115}
                  height={115}
                  quality={100}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {openModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.fullScreenGallery}>
            <div className={styles.closeButtonWrapper}>
              <XMarkIcon
                className={styles.closeButton}
                onClick={handleCloseModal}
              />
            </div>
            <div className={styles.fullScreenPhotoWrapper}>
              <Image
                src={images[slideNumber].url}
                alt={`Zdjęcie produktu ${title}`}
                width={960}
                height={720}
                quality={100}
                className={styles.img}
              />
            </div>
            <div className={styles.fullScreenControls}>
              <ChevronLeftIcon
                onClick={prevSlide}
                className={styles.controlButton}
              />
              <ChevronRightIcon
                onClick={nextSlide}
                className={styles.controlButton}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const INITIAL_VARIANT_POSITION = 0;
const IMAGES_PER_PRODUCT = 3;

export default ProductGallery;
